import { useEffect, useState } from "react"
import { Check, Copy, Link2 } from "lucide-react"
import { api } from "../../api/client"
import { useApp } from "../../state/AppState"
import { Button, Modal, Select, Spinner, cx } from "../ui"
import { copyText } from "../../lib/format"

/**
 * Export the thread as plain text.
 *
 * Plain text, not Markdown or HTML, because the destination is an email client
 * or a group chat that will strip anything cleverer. Every issue carries a link
 * back to the page where it can actually be answered, which is the whole point:
 * the pasted block is a summary, not a fork of the conversation.
 */
export function ShareDialog({ open, onClose, projectId, issueId, projectName }) {
	const { toast } = useApp()
	const [scope, setScope] = useState("all")
	const [payload, setPayload] = useState(null)
	const [loading, setLoading] = useState(false)
	const [copied, setCopied] = useState(null)

	const wholeProject = !issueId

	useEffect(() => {
		if (!open) {
			setPayload(null)
			setCopied(null)
			return
		}
		let cancelled = false
		setLoading(true)

		const request = wholeProject
			? api.exportProject(projectId, scope === "all" ? undefined : scope)
			: api.exportIssue(projectId, issueId)

		request
			.then((result) => {
				if (!cancelled) setPayload(result)
			})
			.catch((error) => toast(error.message, "error"))
			.finally(() => {
				if (!cancelled) setLoading(false)
			})

		return () => {
			cancelled = true
		}
	}, [open, scope, projectId, issueId, wholeProject, toast])

	async function copy(what, text) {
		const ok = await copyText(text)
		if (ok) {
			setCopied(what)
			window.setTimeout(() => setCopied(null), 2000)
		} else {
			toast("Could not reach the clipboard - select the text and copy manually.", "error")
		}
	}

	return (
		<Modal
			open={open}
			onClose={onClose}
			title={wholeProject ? "Share this thread" : "Share this issue"}
			description="Paste it into the email chain. Every line links back here."
			size="lg"
			footer={
				<>
					<Button
						icon={copied === "link" ? Check : Link2}
						onClick={() => copy("link", payload?.url || "")}
						disabled={!payload}
					>
						{copied === "link" ? "Link copied" : "Copy link only"}
					</Button>
					<Button
						variant="primary"
						icon={copied === "text" ? Check : Copy}
						onClick={() => copy("text", payload?.text || "")}
						disabled={!payload}
					>
						{copied === "text" ? "Copied" : "Copy full thread"}
					</Button>
				</>
			}
		>
			<div className="space-y-3">
				{wholeProject && (
					<div className="flex items-center gap-2">
						<label
							htmlFor="share-scope"
							className="text-[0.8125rem] font-medium text-ink-700"
						>
							Include
						</label>
						<Select
							id="share-scope"
							value={scope}
							onChange={(event) => setScope(event.target.value)}
							className="w-auto min-w-[11rem]"
						>
							<option value="all">Everything</option>
							<option value="open">Open issues only</option>
							<option value="closed">Resolved issues only</option>
						</Select>
						<span className="ml-auto text-2xs text-ink-500">
							{projectName}
						</span>
					</div>
				)}

				{loading ? (
					<Spinner label="Building the export" />
				) : (
					<pre
						className={cx(
							"max-h-[50vh] overflow-y-auto rounded-lg border border-ink-200 bg-ink-50",
							// Wrap visually so nothing is hidden off the right edge; the
							// copied text still carries the original line breaks.
							"whitespace-pre-wrap break-words",
							"px-3.5 py-3 font-mono text-[0.75rem] leading-relaxed text-ink-700 scrollbar-slim"
						)}
					>
						{payload?.text || ""}
					</pre>
				)}
			</div>
		</Modal>
	)
}
